/**
 * 用語を指す欄の宣言 — `USES_TERM` の導出（`term-usage.ts`）と用語集の参照の数え方
 * （`queries/elements/term-view.ts`）が同じ答えを出すための 1 か所（設計書 nodes-edges §2 の
 * `USES_TERM`「用語を参照する全型 → Term。欄の参照と、説明文からの抽出」の欄の側）。
 *
 * 以前は同じ「用語を指す欄」を 2 つの表が別々に持ち、片方にしか無い欄（業務の到達の印・ステージの
 * 入口と出口の結末・業務フローの枝の結末と引き金・画面の部品・部品の画面入出力項目）の参照が、
 * 用語集か `USES_TERM` のどちらかから静かに落ちていた。業務ルールの中身も、キーの決め方の中の
 * 参照がどちらからも落ちていた（K36）。
 *
 * **欄へ至る道は表で宣言する。** 値を深く走査して整数を全部拾う形は採らない。集約の中の局所 id
 * （判定表の行・フローの節点・並べ替えのキーの順位）が**正の整数**なので（`localElementId`）、素の深走査
 * では局所 id が同じ番号の用語への参照に化ける。宣言した道だけを歩けば、その取り違えは起きない。
 * 業務ルールだけは道を表に持たず、中身の在り処を `collectRuleTermReferences`（`rules.ts`）から引く。
 *
 * **番号でない値は拾わない。** 欄の形の検査は書込（契約の入力スキーマ）と監査が持つ。ここで
 * 投げると、1 つの欄の崩れで確定そのものが止まる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語を指す欄を型ごとに宣言し、ノード 1 件が指している用語を欄の名前つきで集める（USES_TERM の導出と用語集の参照の数え方の単一ソース）
 * @graph-connects collectRuleTermReferences [calls] 業務ルールが用語を指している参照
 */
import { DECISION_TABLE_RANGE_COMPARATORS, } from '../../contract/elements/index.js';
import { collectRuleTermReferences } from './rules.js';
/**
 * 欄のかたまりの中の 1 欄を、オブジェクトとして読む。オブジェクトでなければ空。
 * @graph-connects none
 */
function recordAt(properties, key) {
    const raw = properties[key];
    return raw !== null && typeof raw === 'object' && !Array.isArray(raw)
        ? raw
        : {};
}
/**
 * 出力の区分が意味を持つか。出さない業務（`outputCount: 'none'`）に残った区分は、何も出さないので
 * 参照に数えない。
 * @graph-connects none
 */
function producesOutput(properties) {
    return properties['outputCount'] !== 'none';
}
/**
 * 入口の用語が引き金か。用語を指す入口は時間（`timer`）と外部の出来事（`external`）の 2 種である
 * （操作の引き金はアクターを指す）。
 * @graph-connects recordAt [calls] 入口の欄を読む
 */
function triggeredByTerm(properties) {
    const kind = recordAt(properties, 'entry')['kind'];
    return kind === 'timer' || kind === 'external';
}
/**
 * 用語を指しうる欄の一覧。**型ごとに、契約のスキーマが持つ用語への参照の欄を並べる。**
 *
 * ここに無い欄は「指していない」と読まれる。欄が増えたのにここへ足さないと、その欄からの参照
 * だけが用語集の数からも `USES_TERM` からも静かに落ちる — 参照が 0 件に見えるのは削除の候補と
 * いう意味なので（用語集の「使用なし」・制約の「被参照 0」）、落とすと消してはいけない語が削除
 * 候補に並ぶ。
 *
 * 載せていない欄には理由がある。
 *
 * - 用語の `unit` と状態遷移の `event` は用語ではなく単位・動詞を指す
 * - 部品の `screen` / `operates` / `visibleWhen` / `transitionTo` / `actions[]` の遷移先は
 *   画面・業務・業務ルールを指す
 * - 業務の `verb` / `actor` / `domain` / `rules`、ステージの `verb` / `entry.business` /
 *   `exit.business`、業務フローの `stage` / `nodes` も同じく用語以外を指す
 * - **業務ルールは表に載せない**（`collectRuleTermReferences` が在り処を持つ）
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語を指しうる欄を型ごとに宣言した表（業務ルール以外。USES_TERM と用語集の単一ソース）
 * @graph-connects none
 */
export const TERM_REFERENCE_PATHS = {
    Term: [
        { path: ['ownerClass'], field: '属するクラス' },
        { path: ['values', '*'], field: '取りうる値' },
        { path: ['transitions', '*', 'from'], field: '状態遷移の今の値' },
        { path: ['transitions', '*', 'to'], field: '状態遷移の次の値' },
    ],
    Actor: [
        { path: ['subject'], field: 'クラス' },
        { path: ['qualifier'], field: '限定' },
    ],
    Domain: [{ path: ['subject'], field: 'クラス' }],
    Stage: [
        { path: ['subject'], field: 'クラス' },
        { path: ['entry', 'ending'], field: '入口の結末' },
        { path: ['exit', 'ending'], field: '出口の結末' },
    ],
    Flow: [
        { path: ['situation'], field: '状況' },
        { path: ['entry', 'term'], field: '引き金', when: triggeredByTerm },
        { path: ['transitions', '*', 'ending'], field: '枝の結末' },
    ],
    Business: [
        { path: ['subject'], field: 'クラス' },
        { path: ['inputs', '*'], field: '入力' },
        { path: ['executedAt'], field: '実行の時点' },
        { path: ['outputClass'], field: '出力の区分', when: producesOutput },
        { path: ['endings', '*', 'result'], field: '結末の結果' },
        { path: ['endings', '*', 'reachedMark', 'property'], field: '到達の印のプロパティ' },
        { path: ['endings', '*', 'reachedMark', 'value'], field: '到達の印の値' },
    ],
    Screen: [
        { path: ['subject'], field: 'クラス' },
        { path: ['elements', '*', 'subject'], field: '部品のクラス' },
    ],
    Component: [
        { path: ['subject'], field: 'クラス' },
        { path: ['io', 'dataType'], field: '画面入出力項目の型' },
        { path: ['io', 'initialValue', 'term'], field: '画面入出力項目の初期値' },
    ],
};
/**
 * 業務ルールの参照の在り処を欄の名前へ写す表。鍵は在り処（`collectRuleReferences` の `path`）から
 * 添字と範囲のセルの比べ方を落とした形（`body.rows[0].cells[1].lt.term` → `body.rows.cells`）。
 * キーの決め方の中（`body.derive[n].…`）は同じ表を引いて「キーの決め方の」を前に付ける。
 * `body.rows.output` はキーの決め方の判定表だけが持つ（行ごとの出力が 1 つ）。
 * @graph-connects none
 */
const RULE_TERM_FIELDS = {
    output: '出力',
    'body.inputs': '判定表の入力',
    'body.outputs': '判定表の出力',
    'body.rows.cells': '判定表のセル',
    'body.rows.outputs': '判定表の行の出力',
    'body.rows.output': '判定表の行の出力',
    'body.steps.name': '式の途中の値',
    'body.steps.expression.operands.term': '式の項',
    'body.steps.expression.operands.when.property': '式の項の条件',
    'body.steps.expression.operands.when.value': '式の項の条件',
    'body.output': '式の出力',
    'body.population': '並べ替えの母集団',
    'body.keys.attribute': '並べ替えのキー',
    'body.keys.order': '並べ替えの値の並び',
    'body.target': '変更の対象',
    'body.to': '変更の変える先',
};
/** キーの決め方の中の在り処の頭（`body.derive[0].`）。 @graph-connects none */
const RULE_DERIVE_PREFIX = /^body\.derive\[\d+\]\./u;
/** 範囲のセルの比べ方と相手（`.lt.term`）。比べ方は契約の宣言から組む。 @graph-connects none */
const RANGE_CELL_OPERAND = new RegExp(`\\.(?:${DECISION_TABLE_RANGE_COMPARATORS.join('|')})\\.term$`, 'u');
/**
 * 表に無い在り処に付ける欄の名前。**参照は落とさない** — 落とすと、その欄だけで使う用語が
 * 「使用なし」に見える。
 * @graph-connects none
 */
const RULE_TERM_FIELD_FALLBACK = '業務ルールの中身';
/**
 * 業務ルールの参照の在り処を、画面に出す欄の名前にする。
 *
 * @param path `collectRuleReferences` が返す在り処
 * @returns 欄の名前。表に無い在り処は「業務ルールの中身」
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務ルールの参照の在り処（キーの決め方の中を含む）を、用語の参照元に出す欄の名前へ写す
 * @graph-connects none
 */
export function ruleTermFieldOf(path) {
    const inDerive = RULE_DERIVE_PREFIX.test(path);
    const rest = path.replace(RULE_DERIVE_PREFIX, '');
    if (inDerive && rest === 'key')
        return 'キーの決め方のキー';
    const field = RULE_TERM_FIELDS[rest.replace(RANGE_CELL_OPERAND, '').replace(/\[\d+\]/gu, '')] ??
        RULE_TERM_FIELD_FALLBACK;
    return inDerive ? `キーの決め方の${field}` : field;
}
/**
 * 宣言した道の終点に在る番号（正の整数）を集める。道の途中が無い・型が違う枝は歩かない。
 *
 * @param value 走査する値
 * @param segments 残りの道
 * @returns 終点に在った番号（現れた順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 宣言した道の終点に在る参照の番号を集める
 * @graph-connects none
 */
export function collectTermRefsAt(value, segments) {
    const head = segments[0];
    if (head === undefined) {
        return typeof value === 'number' && Number.isInteger(value) && value > 0 ? [value] : [];
    }
    const rest = segments.slice(1);
    if (head === '*') {
        if (!Array.isArray(value))
            return [];
        return value.flatMap((item) => collectTermRefsAt(item, rest));
    }
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return [];
    const record = value;
    return head in record ? collectTermRefsAt(record[head], rest) : [];
}
/**
 * ノード 1 件の欄が指している用語を、宣言の順に集める。重複も、指した先が用語かどうかも見ない
 * （用語集は欄ごとに畳んで用語でない番号を捨て、`USES_TERM` は番号で畳む）。
 *
 * @param value ノードの姿
 * @returns 指している用語の番号と欄の名前（宣言の順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ノード 1 件の欄から、指している用語の番号と欄の名前を宣言の順に集める
 * @graph-connects collectRuleTermReferences [calls] 業務ルールが用語を指している参照
 * @graph-connects ruleTermFieldOf [calls] 業務ルールの在り処を欄の名前へ
 * @graph-connects collectTermRefsAt [calls] 宣言した道の終点の番号
 */
export function collectTermReferences(value) {
    if (value.nodeType === 'Rule') {
        return collectRuleTermReferences(value.properties).map((reference) => ({
            termId: reference.id,
            field: ruleTermFieldOf(reference.path),
        }));
    }
    return (TERM_REFERENCE_PATHS[value.nodeType] ?? []).flatMap(({ path, field, when }) => when === undefined || when(value.properties)
        ? collectTermRefsAt(value.properties, path).map((termId) => ({ termId, field }))
        : []);
}
//# sourceMappingURL=term-references.js.map